import type { ComponentPropsWithRef } from 'react';

import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { useConditionsWithTrashed, useCustomConditionsWithTrashed } from '~/api/conditions';
import { Button, Card, Dialog, Icon, Illustration, InfoBlock, Separator, Text } from '~/components';
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard';
import { clsx } from '~/styles/utils';

import { ConditionPdfDialog } from '../ConditionPdfDialog/ConditionPdfDialog';
import {
  checkIconStyle,
  checklistItemStyle,
  checklistStyle,
  conditionNameStyle,
  conditionStyle,
  conditionTextStyle,
  copySpecialConditionsStyle,
  illustrationStyle,
  noConditionStyle,
  rootStyle,
  triggerStyle,
  unavailableConditionStyle,
} from './CopyrightCard.css';

type Props = Omit<ComponentPropsWithRef<typeof Card.Root>, 'render' | 'size' | 'variant'> & {
  conditionId: number | null;
  customAgreementId: number | null;
  customConditionId: number | null;
  specialConditions: string | null;
};

export const CopyrightCard = ({ conditionId, customAgreementId, customConditionId, specialConditions, ...delegated }: Props) => {
  const { data: conditions } = useConditionsWithTrashed();
  const { data: customConditions } = useCustomConditionsWithTrashed();

  const [isCopied, copyToClipboard] = useCopyToClipboard();

  const { t } = useTranslation(['common', 'invoices']);

  const condition = conditions.find(({ id }) => id === conditionId) ?? null;
  const customCondition = customConditions.find(({ id }) => id === customConditionId) ?? null;

  const type =
    conditionId !== null
      ? 'condition'
      : customConditionId !== null
        ? 'customCondition'
        : customAgreementId !== null
          ? 'customAgreement'
          : 'none';

  const isConditionAvailable = match(type)
    .with('condition', () => condition !== null)
    .with('customCondition', () => customCondition !== null)
    .otherwise(() => true);

  const standardConditionId = type === 'condition' && isConditionAvailable ? conditionId : null;

  const checklistItems = match(condition?.id)
    .with(8, () => t('invoices:details.summary.copyright.conditionsChecklist.transfer', { returnObjects: true }))
    .with(31, () => t('invoices:details.summary.copyright.conditionsChecklist.rightOfUse', { returnObjects: true }))
    .with(34, () => t('invoices:details.summary.copyright.conditionsChecklist.onlineCourse', { returnObjects: true }))
    .otherwise(() => null);
  const checklist = checklistItems === null || typeof checklistItems === 'string' ? [] : Object.values(checklistItems);

  const name = match(type)
    .with('condition', () => condition?.name ?? t('invoices:details.summary.copyright.notAvailable'))
    .with('customCondition', () => customCondition?.name ?? t('invoices:details.summary.copyright.notAvailable'))
    .with('customAgreement', () => t('common:customAgreementOptionLabel'))
    .with('none', () => t('invoices:details.summary.copyright.noConditions'))
    .exhaustive();

  const supportsClipboardApi = 'clipboard' in navigator;

  const copySpecialConditions = () => {
    if (specialConditions) copyToClipboard(specialConditions);
  };

  return (
    <Card.Root {...delegated} className={clsx(rootStyle, delegated.className)} size="small" variant="primary-subtle">
      <div className={conditionStyle}>
        {standardConditionId !== null && <Illustration className={illustrationStyle} name="Stamp" strokeWidth={3} />}

        <div className={conditionTextStyle}>
          <p className={clsx(conditionNameStyle, !isConditionAvailable && unavailableConditionStyle, type === 'none' && noConditionStyle)}>
            {!isConditionAvailable && <Icon name="Error" size={20} />}

            {name}
          </p>

          {type !== 'none' && <Text>{t('invoices:details.copyright.subtitle')}</Text>}
        </div>
      </div>

      {standardConditionId !== null && (
        <>
          <ul className={checklistStyle}>
            {checklist.map((item, index) => (
              <li className={checklistItemStyle} key={index}>
                <Icon className={checkIconStyle} name="Check" />
                {item}
              </li>
            ))}
          </ul>

          <Dialog.Root>
            <Dialog.Trigger
              render={
                <Button className={triggerStyle} emphasis="tertiary" icon="File">
                  {t('invoices:details.copyright.viewDocument')}
                </Button>
              }
            />

            <ConditionPdfDialog conditionId={standardConditionId} />
          </Dialog.Root>
        </>
      )}

      {!!specialConditions && (
        <>
          <Separator />

          <InfoBlock
            action={
              customAgreementId === null &&
              supportsClipboardApi && (
                <Button
                  aria-label={isCopied ? t('common:actions.copiedToClipboard') : t('common:actions.copyToClipboard')}
                  className={copySpecialConditionsStyle}
                  disabled={isCopied}
                  emphasis="tertiary"
                  icon={isCopied ? 'Check' : 'Copy'}
                  onClick={copySpecialConditions}
                />
              )
            }
            label={t('invoices:fields.specialConditions.label')}
            variant="caps"
          >
            <Text>{specialConditions}</Text>
          </InfoBlock>
        </>
      )}
    </Card.Root>
  );
};
